import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ActionMeta } from 'react-select';
import Popover, { PopoverBody } from '@lib/components/Popover/Popover';
import { TabBarItem } from '@lib/components/TabBar/constants';
import useSelectPanelOptions from './hooks/useSelectPanelOptions';
import { SelectOption, SelectPanelProps } from './types';
import {
  createSelectedOptionsMap,
  getComparableKey,
  getOptionLabel,
} from './utils';
import {
  DEFAULT_LIST_HEIGHT,
  DEFAULT_SEARCH_DELAY,
  TAB_ALL,
  TAB_SELECTED,
} from './constants';
import { SelectPanelButton } from './components/SelectPanelButton';
import { SelectPanelHeader } from './components/SelectPanelHeader';
import { SelectPanelCreateAction } from './components/SelectPanelCreateAction';
import { SelectPanelOptionsList } from './components/SelectPanelOptionsList';
import { SelectPanelStates } from './components/SelectPanelStates';
import styles from './SelectPanel.module.scss';

function SelectPanel(props: SelectPanelProps) {
  const {
    allowEmptySelection = false,
    autoLoadOnScroll = true,
    buttonProps,
    label,
    className,
    closeOnSelect,
    createNewOptionProps,
    defaultOptions = false,
    defaultValue = null,
    disabled,
    disabledTooltipProps,
    emptyStateTitle,
    getLoadOptions,
    isMulti = false,
    isOptionDisabled,
    listAriaLabel,
    maxListHeight = DEFAULT_LIST_HEIGHT,
    name = '',
    noResultsTitle,
    onChange,
    options = [],
    panelClassName,
    popoverProps,
    renderOption,
    scrollable = true,
    searchDelay = DEFAULT_SEARCH_DELAY,
    searchPlaceholder,
    searchable = true,
    showTabs = false,
    tabsLabels,
    value,
    values,
  } = props;

  const { t } = useTranslation();
  const listboxId = useId();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(TAB_ALL);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
    null,
  );
  const hasLoadedOnceAllRef = useRef(false);
  const hasLoadedOnceSelectedRef = useRef(false);
  const lastSearchAllRef = useRef('');
  const lastSearchSelectedRef = useRef('');
  const [internalValue, setInternalValue] = useState<
    SelectOption | SelectOption[] | null
  >(defaultValue);
  const isControlled = value !== undefined;

  useEffect(() => {
    if (!isControlled) {
      setInternalValue(defaultValue);
    }
  }, [defaultValue, isControlled]);

  const resolvedValue = (isControlled ? value : internalValue) ?? null;

  const selectedOptions = useMemo<SelectOption[]>(() => {
    if (isMulti) {
      if (Array.isArray(resolvedValue)) return resolvedValue;
      return resolvedValue ? [resolvedValue] : [];
    }
    if (Array.isArray(resolvedValue)) {
      return resolvedValue.length ? [resolvedValue[0]] : [];
    }
    return resolvedValue ? [resolvedValue] : [];
  }, [isMulti, resolvedValue]);

  // Hook for "All" tab - loads all available options
  const {
    hasMore: hasMoreAll,
    isAsync,
    isLoading: isLoadingAll,
    resetCounter: resetCounterAll,
    loadInitial: loadInitialAll,
    loadMore: loadMoreAll,
    options: allTabOptions,
  } = useSelectPanelOptions({
    getLoadOptions,
    name,
    options,
    values,
  });

  // Hook for "Selected" tab - loads only selected options
  const {
    hasMore: hasMoreSelected,
    isLoading: isLoadingSelected,
    resetCounter: resetCounterSelected,
    loadInitial: loadInitialSelected,
    loadMore: loadMoreSelected,
    options: selectedTabOptions,
  } = useSelectPanelOptions({
    getLoadOptions,
    name: `${name}-selected`,
    options,
    values,
  });

  const availableOptions = isAsync ? allTabOptions : options;

  const tabsEnabled = showTabs && isMulti;
  const effectiveTab = tabsEnabled ? activeTab : TAB_ALL;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, searchDelay);
    return () => clearTimeout(timer);
  }, [searchQuery, searchDelay]);

  useEffect(() => {
    if (!isAsync) return;
    const resetCounter =
      effectiveTab === TAB_SELECTED ? resetCounterSelected : resetCounterAll;
    if (!resetCounter) return;

    const hasLoadedOnceRef =
      effectiveTab === TAB_SELECTED
        ? hasLoadedOnceSelectedRef
        : hasLoadedOnceAllRef;
    const lastSearchRef =
      effectiveTab === TAB_SELECTED ? lastSearchSelectedRef : lastSearchAllRef;

    hasLoadedOnceRef.current = false;
    lastSearchRef.current = '';
  }, [isAsync, resetCounterAll, resetCounterSelected, effectiveTab]);

  useEffect(() => {
    if (!isAsync || !isPanelOpen) return;
    const searchKey = debouncedSearch || '';
    const isSelectedTab = effectiveTab === TAB_SELECTED;

    const hasLoadedOnceRef = isSelectedTab
      ? hasLoadedOnceSelectedRef
      : hasLoadedOnceAllRef;
    const lastSearchRef = isSelectedTab
      ? lastSearchSelectedRef
      : lastSearchAllRef;
    const loadFn = isSelectedTab ? loadInitialSelected : loadInitialAll;

    const isFirstLoad = !hasLoadedOnceRef.current;
    const wasSearching = lastSearchRef.current !== '';
    const isClearingSearch = wasSearching && searchKey === '';
    const allowEmptySearchReload =
      defaultOptions || isFirstLoad || Boolean(searchKey) || isClearingSearch;
    if (!allowEmptySearchReload) return;
    if (!isFirstLoad && lastSearchRef.current === searchKey) return;

    loadFn(searchKey);
    lastSearchRef.current = searchKey;
    hasLoadedOnceRef.current = true;
  }, [
    debouncedSearch,
    defaultOptions,
    isAsync,
    isPanelOpen,
    loadInitialAll,
    loadInitialSelected,
    effectiveTab,
  ]);

  useEffect(() => {
    if (!isPanelOpen) return;
    if (searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    } else if (panelRef.current) {
      panelRef.current.focus();
    }
  }, [isPanelOpen, searchable]);

  // Reset to "All" tab when panel opens
  useEffect(() => {
    if (isPanelOpen) {
      setActiveTab(TAB_ALL);
    }
  }, [isPanelOpen]);

  useEffect(() => {
    if (!showTabs || !selectedOptions.length) {
      setActiveTab(TAB_ALL);
    }
  }, [selectedOptions.length, showTabs]);

  const buttonCounterFromProps = buttonProps?.counter;
  let computedButtonCounter: number | undefined;
  if (buttonCounterFromProps !== undefined) {
    computedButtonCounter = buttonCounterFromProps;
  } else if (isMulti && selectedOptions.length) {
    computedButtonCounter = selectedOptions.length;
  } else {
    computedButtonCounter = undefined;
  }

  // Determine which data source to use based on active tab
  const listSource = useMemo(() => {
    if (effectiveTab === TAB_SELECTED) {
      return isAsync ? selectedTabOptions : selectedOptions;
    }
    return availableOptions;
  }, [
    effectiveTab,
    isAsync,
    selectedTabOptions,
    selectedOptions,
    availableOptions,
  ]);

  // In async mode, filtering is done server-side via loadOptions
  // In sync mode, apply client-side filtering
  const filteredOptions = useMemo(() => {
    if (isAsync) {
      // For async, server handles filtering, just return the list
      return listSource;
    }
    // For sync (static options), apply client-side filtering
    if (!debouncedSearch) return listSource;
    const lowered = debouncedSearch.toLowerCase();
    return listSource.filter((option) =>
      getOptionLabel(option, t).toLowerCase().includes(lowered),
    );
  }, [debouncedSearch, isAsync, listSource, t]);

  const selectedMap = useMemo(
    () => createSelectedOptionsMap(selectedOptions),
    [selectedOptions],
  );

  const tabItems: TabBarItem[] = useMemo(() => {
    if (!tabsEnabled) return [];
    const items: TabBarItem[] = [
      {
        value: TAB_ALL,
        label: tabsLabels?.all ?? t('all', { defaultValue: 'All' }),
      },
      {
        value: TAB_SELECTED,
        label:
          tabsLabels?.selected ?? t('selected', { defaultValue: 'Selected' }),
        counter: selectedOptions.length || undefined,
        disabled: selectedOptions.length === 0,
      },
    ];
    return items;
  }, [selectedOptions.length, tabsLabels, t, tabsEnabled]);

  const closePanelState = useCallback((handleClose: () => void) => {
    setIsPanelOpen(false);
    handleClose();
  }, []);

  const emitChange = useCallback(
    (
      nextValue: SelectOption | SelectOption[] | null,
      action: ActionMeta<SelectOption>['action'],
      metaOption?: SelectOption,
      removedValue?: SelectOption,
    ) => {
      const actionMeta = {
        action,
        option: metaOption,
        removedValue,
        name,
      } as ActionMeta<SelectOption>;
      if (!isControlled) {
        setInternalValue(nextValue);
      }
      onChange?.(nextValue, actionMeta);
    },
    [isControlled, name, onChange],
  );

  const handleOptionSelect = useCallback(
    (option: SelectOption, handleClose: () => void) => {
      const optionKey = getComparableKey(option);
      const alreadySelected = selectedMap.has(optionKey);
      if (isMulti) {
        let nextValue: SelectOption[];
        if (alreadySelected) {
          nextValue = selectedOptions.filter(
            (selected) => getComparableKey(selected) !== optionKey,
          );
        } else {
          nextValue = [...selectedOptions, option];
        }
        emitChange(
          nextValue,
          alreadySelected ? 'deselect-option' : 'select-option',
          option,
          alreadySelected ? option : undefined,
        );
        return;
      }
      if (alreadySelected && allowEmptySelection) {
        emitChange(null, 'clear', option, option);
        return;
      }
      emitChange(option, 'select-option', option);
      const shouldClose =
        closeOnSelect !== undefined ? closeOnSelect : !isMulti;
      if (shouldClose) {
        closePanelState(handleClose);
      }
    },
    [
      allowEmptySelection,
      closeOnSelect,
      closePanelState,
      emitChange,
      isMulti,
      selectedMap,
      selectedOptions,
    ],
  );

  const isLoadingMoreRef = useRef(false);

  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const isSelectedTab = effectiveTab === TAB_SELECTED;
    const hasMore = isSelectedTab ? hasMoreSelected : hasMoreAll;
    const isLoading = isSelectedTab ? isLoadingSelected : isLoadingAll;
    const loadMore = isSelectedTab ? loadMoreSelected : loadMoreAll;

    if (!autoLoadOnScroll || !scrollable || !isAsync || !hasMore || isLoading) {
      return;
    }
    // Prevent multiple simultaneous loadMore calls
    if (isLoadingMoreRef.current) {
      return;
    }
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 32) {
      isLoadingMoreRef.current = true;
      loadMore(debouncedSearch);
      // Reset the flag after a short delay to allow next load
      setTimeout(() => {
        isLoadingMoreRef.current = false;
      }, 500);
    }
  };

  const handleTrigger = (handleToggle: (next?: boolean) => void) => {
    const nextState = !isPanelOpen;
    setIsPanelOpen(nextState);
    handleToggle(nextState);
  };

  const handleReset = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (disabled) return;
    if (!selectedOptions.length) return;
    const nextValue = isMulti ? [] : null;
    const removedValue = !isMulti ? selectedOptions[0] : undefined;
    emitChange(nextValue, 'clear', undefined, removedValue);
  };

  const popoverBody: PopoverBody = ({ handleClose }) => {
    const isSelectedTab = effectiveTab === TAB_SELECTED;
    const isLoading = isSelectedTab ? isLoadingSelected : isLoadingAll;

    const showEmptyState =
      !filteredOptions.length && !debouncedSearch && !isLoading;
    const showNoResults =
      !filteredOptions.length && !!debouncedSearch && !isLoading;

    return (
      <div
        className={classNames(styles.panel, panelClassName)}
        ref={panelRef}
        tabIndex={-1}
        role="presentation"
      >
        <SelectPanelHeader
          activeTab={activeTab}
          name={name}
          onSearchChange={setSearchQuery}
          onTabChange={(item) => setActiveTab(String(item.value))}
          searchable={searchable}
          searchPlaceholder={searchPlaceholder}
          searchQuery={searchQuery}
          searchInputRef={searchInputRef}
          showTabs={tabsEnabled}
          tabItems={tabItems}
        />
        {createNewOptionProps && (
          <SelectPanelCreateAction
            createNewOptionProps={createNewOptionProps}
          />
        )}
        <SelectPanelOptionsList
          debouncedSearch={debouncedSearch}
          filteredOptions={filteredOptions}
          isLoading={isLoading}
          isMulti={isMulti}
          isOptionDisabled={isOptionDisabled}
          listAriaLabel={listAriaLabel}
          listboxId={listboxId}
          maxListHeight={maxListHeight}
          onOptionClick={(option) => handleOptionSelect(option, handleClose)}
          onScroll={handleScroll}
          renderOption={renderOption}
          scrollable={scrollable}
          selectedMap={selectedMap}
        />
        {showEmptyState && (
          <SelectPanelStates
            title={emptyStateTitle}
            fallbackTitle={t('not-found')}
          />
        )}
        {showNoResults && (
          <SelectPanelStates
            title={noResultsTitle}
            fallbackTitle={t('no-items-available')}
          />
        )}
      </div>
    );
  };

  const renderTrigger = SelectPanelButton({
    buttonProps,
    computedButtonCounter,
    disabled,
    disabledTooltipProps,
    isPanelOpen,
    label,
    onReset: handleReset,
    onTrigger: handleTrigger,
  });

  return (
    <div className={classNames(styles.root, className)}>
      <Popover
        trigger="click"
        {...popoverProps}
        closeCallback={() => {
          setIsPanelOpen(false);
          popoverProps?.closeCallback?.();
        }}
        body={popoverBody}
        width={popoverProps?.width ?? 320}
      >
        {renderTrigger}
      </Popover>
    </div>
  );
}

export default SelectPanel;
